import { useCallback, useEffect, useState } from "react";
import { Button, Card, message, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { DeleteOutlined, GoogleOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { googleWorkspaceApi } from "../services/googleWorkspaceApi";

const { Text, Title } = Typography;

const formatDateTime = (value) => {
  const date = value ? moment(value) : null;
  return date?.isValid() ? date.format("DD/MM/YYYY HH:mm") : "—";
};

export default function GoogleWorkspace() {
  const location = useLocation();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [checkingId, setCheckingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await googleWorkspaceApi.connections();
      setConnections(Array.isArray(result) ? result : []);
    } catch (error) { message.error(error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const status = new URLSearchParams(location.search).get("status");
    if (status === "success") message.success("Đã kết nối Google Drive và Google Sheets");
    if (status === "error") message.error("Không thể kết nối Google Workspace");
  }, [location.search]);

  const connect = async () => {
    try {
      const result = await googleWorkspaceApi.connect();
      window.location.assign(result.authorizationUrl);
    } catch (error) { message.error(error.message); }
  };

  const disconnect = async (connection) => {
    setDisconnectingId(connection.id);
    try {
      await googleWorkspaceApi.disconnectConnection(connection.id);
      await load();
      message.success("Đã ngắt kết nối Google Workspace");
    } catch (error) { message.error(error.message); }
    finally { setDisconnectingId(null); }
  };

  const checkConnection = async (connection) => {
    setCheckingId(connection.id);
    try {
      const result = await googleWorkspaceApi.checkConnection(connection.id);
      await load();
      if (result?.connected) message.success(result.message || "Kết nối Google Drive hoạt động bình thường.");
      else message.error(result?.message || "Không thể xác thực kết nối Google.");
    } catch (error) {
      await load();
      message.error(error.message || "Không thể kiểm tra kết nối Google.");
    } finally { setCheckingId(null); }
  };

  const columns = [
    { title: "GOOGLE WORKSPACE", dataIndex: "email", render: (email) => <Text strong>{email || "Tài khoản Google"}</Text> },
    {
      title: "TRẠNG THÁI", width: 150, render: (_, item) => {
        const connected = item.connected === true || item.status === "ACTIVE";
        return <Tag color={connected ? "green" : "red"}>{connected ? "Đã kết nối" : item.status || "Mất kết nối"}</Tag>;
      },
    },
    { title: "CẬP NHẬT", width: 170, render: (_, item) => formatDateTime(item.updatedAt) },
    { title: "LỖI GẦN NHẤT", dataIndex: "lastError", render: (value) => <div style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{value || "—"}</div> },
    {
      title: "THAO TÁC", width: 280, render: (_, item) => <Space wrap>
        <Button aria-label="Kiểm tra kết nối" size="small" icon={<ReloadOutlined />} loading={checkingId === item.id} disabled={Boolean(disconnectingId)} onClick={() => checkConnection(item)}>Kiểm tra kết nối</Button>
        <Popconfirm title="Ngắt kết nối workspace này?" okText="OK" cancelText="Hủy" onConfirm={() => disconnect(item)}>
          <Button aria-label="Ngắt kết nối" danger size="small" icon={<DeleteOutlined />} loading={disconnectingId === item.id} disabled={Boolean(checkingId)}>Ngắt kết nối</Button>
        </Popconfirm>
      </Space>,
    },
  ];

  return <>
    <div className="page-heading">
      <div><Title level={3}>Google Drive & Sheets</Title><Text type="secondary">Quản lý các Google Workspace đã kết nối của tài khoản hiện tại.</Text></div>
      <Button type="primary" icon={<PlusOutlined />} onClick={connect}>Kết nối workspace mới</Button>
    </div>
    <Card className="criclebox" title={<Space><GoogleOutlined /> Kết nối Google Workspace</Space>}>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={connections} pagination={false} locale={{ emptyText: "Chưa có Google Workspace nào được kết nối" }} scroll={{ x: 900 }} />
    </Card>
  </>;
}
